
import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { MatCard, MatCardContent } from '@angular/material/card';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';

import { FsAcl, FsAclModule } from '@firestitch/acl';
import { FsBadgeModule } from '@firestitch/badge';
import { FsListComponent, FsListConfig, FsListModule } from '@firestitch/list';
import { FsSelectButtonModule } from '@firestitch/selectbutton';

import { filter, map, takeUntil } from 'rxjs/operators';

import { ProjectData } from '@common/data';
import { AclPermission, State } from '@common/enums';
import { Project } from '@common/interfaces';
import { NavService, SessionService } from '@common/services';

import { ProjectCreateComponent } from '@libs/project-create/components';

import {
  ProjectFilterConfigService,
} from '@app/common';

import { StatusPillComponent } from '../../../../libs/pills/status/components/status-pill/status-pill.component';


@Component({
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],
  providers: [
    ProjectFilterConfigService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    FsListModule,
    FsBadgeModule,
    StatusPillComponent,
    RouterLink,
    FsAclModule,
    MatSelect,
    FsSelectButtonModule,
    FormsModule,
    MatOption,
  ],
})
export class ProjectsComponent implements OnInit, OnDestroy {

  @ViewChild(FsListComponent)
  public list: FsListComponent = null;

  public config: FsListConfig = null;
  public links: { name: string, value: string, permission: AclPermission }[] = [];
  public environmentId: number = null;

  private _destroy$ = new EventEmitter<void>();
  private _projectData = inject(ProjectData);
  private _navService = inject(NavService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _acl = inject(FsAcl);
  private _sessionService = inject(SessionService);
  private _projectFilterConfigService = inject(ProjectFilterConfigService);

  public ngOnInit(): void {
    this.environmentId = this._sessionService.environmentId();
    const hasPermissions = this._acl.has(AclPermission.Environment)
      && this._acl
        .hasWrite(AclPermission.Project, [this._sessionService.environmentId()]);
    this._setTitle();
    this._initLinks();

    this.config = {
      sorts: [
        { name: 'Recent', value: 'recent', direction: 'desc' },
      ],
      sort: { value: 'recent', direction: 'desc' },
      filters: this._projectFilterConfigService.filters,
      actions: [
        {
          label: 'Create',
          show: () => hasPermissions,
          click: () => {
            const dialogRef = this._dialog.open(ProjectCreateComponent, {
              data: {},
            });

            dialogRef.afterClosed()
              .pipe(
                filter((response) => !!response),
                takeUntil(this._destroy$),
              )
              .subscribe((response) => {
                this._router.navigate(['../projects', response.id], { relativeTo: this._route });
              });
          },
        },
      ],
      rowActions: [
        {
          click: (data) => {
            return this._projectData.delete(data);
          },
          show: (data) => hasPermissions
            && data.state !== 'deleted',
          remove: {
            title: 'Confirm',
            template: 'Are you sure you would like to delete this record?',
          },
          label: 'Delete',
        },
      ],
      fetch: (query) => {
        query.statuses = true;

        // When each project was last opened is the caller's own, so the API takes it as recent, not as an order
        const [sort, direction] = String(query.order || '').split(',');
        if (sort === 'recent') {
          delete query.order;
          query.recent = direction || 'desc';
        }

        if (this._sessionService.environment()) {
          query.environmentId = this._sessionService.environment().id;
        }

        return this._projectData.gets(query, { key: null })
          .pipe(
            map((response) => ({ data: response.projects, paging: response.paging })),
          );
      },
    };

    if (hasPermissions) {
      this.config.restore = {
        query: { state: State.Deleted },
        filter: true,
        menuLabel: 'Restore',
        click: (row) => {
          return this._projectData.put({ id: row.id, state: State.Active });
        },
      };
    }
  }

  public goToChanged(row: Project, value: string): void {
    this._router.navigate(['../projects', row.id, value], { relativeTo: this._route });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _setTitle(): void {
    this._navService.setTitle('Projects', '', '');
  }

  private _initLinks(): void {
    this.links = [
      {
        name: 'Tasks',
        value: 'tasks',
        permission: AclPermission.Task,
      },
      {
        name: 'Board',
        value: 'board',
        permission: AclPermission.Task,
      },
      {
        name: 'Plans',
        value: 'plans',
        permission: AclPermission.Plan,
      },
      {
        name: 'Docs',
        value: 'docs',
        permission: AclPermission.Doc,
      },
      {
        name: 'Mockups',
        value: 'images',
        permission: AclPermission.Image,
      },
      {
        name: 'Designs',
        value: 'design',
        permission: AclPermission.Design,
      },
      {
        name: 'Data',
        value: 'data',
        permission: AclPermission.Data,
      },
      {
        name: 'Scenarios',
        value: 'scenarios',
        permission: AclPermission.Scenario,
      },
      {
        name: 'KB',
        value: 'kbs',
        permission: AclPermission.Kb,
      },
      {
        name: 'Test Cases',
        value: 'testcases',
        permission: AclPermission.TestCase,
      },
      {
        name: 'Artifacts',
        value: 'artifacts',
        permission: null,
      },
      {
        name: 'Meetings',
        value: 'meetings',
        permission: AclPermission.Meeting,
      },
      {
        name: 'Settings',
        value: 'settings',
        permission: AclPermission.Project,
      },
    ];
  }

}
